import React, { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { rememberReturnTo, resolveReturnTo, registerHref } from "@/lib/authReturn";
import { Loader2 } from "lucide-react";
import AuthShell from "@/pages/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";

export default function Login() {
  const { login, error, startGoogleLogin, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const dest = resolveReturnTo(location);
  useEffect(() => { rememberReturnTo(dest); }, [dest]);
  if (!loading && isAuthenticated) return <Navigate to={dest} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLocalErr(null);
    setBusy(true);
    const ok = await login(email.trim(), password);
    setBusy(false);
    if (ok) navigate(dest, { replace: true });
  };

  const errMsg = localErr || error;

  return (
    <AuthShell>
      <div className="max-w-md w-full mx-auto">
        <div className="font-display uppercase text-[11px] tracking-[0.4em] text-gold mb-3">
          Sign In
        </div>
        <h1 className="font-display uppercase text-3xl md:text-4xl tracking-tight text-white">
          Welcome back to <span className="text-gold-sheen">the Square</span>
        </h1>
        <p className="mt-3 text-white/60 text-sm">
          The song comes first — sign in to keep the craft moving.
        </p>

        <form
          onSubmit={submit}
          data-testid={TID.auth.loginForm}
          className="mt-10 space-y-5"
          noValidate
        >
          <div className="space-y-2">
            <Label className="font-display uppercase text-[11px] tracking-[0.35em] text-white/70">
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid={TID.auth.loginEmail}
              required
              placeholder="you@studio.com"
              className="rounded-none bg-matte-card border-white/10 focus-visible:border-gold focus-visible:ring-gold/40 h-11 text-white placeholder:text-white/30"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-display uppercase text-[11px] tracking-[0.35em] text-white/70">
              Password
            </Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid={TID.auth.loginPassword}
              required
              placeholder="••••••••"
              className="rounded-none bg-matte-card border-white/10 focus-visible:border-gold focus-visible:ring-gold/40 h-11 text-white placeholder:text-white/30"
            />
          </div>

          {errMsg && (
            <div
              data-testid={TID.auth.loginError}
              className="border border-red-500/40 bg-red-500/10 text-red-200 px-4 py-3 text-sm"
            >
              {errMsg}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid={TID.auth.loginSubmit}
            className="w-full rounded-none h-11 bg-goldSheen text-black font-display uppercase tracking-widest hover:brightness-110 shadow-goldSoft hover:shadow-goldGlow border border-gold/60 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>

        <div className="my-8 flex items-center gap-4">
          <div className="h-px flex-1 bg-white/10" />
          <span className="font-display uppercase text-[10px] tracking-[0.4em] text-white/40">
            or
          </span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        <Button
          type="button"
          onClick={startGoogleLogin}
          data-testid={TID.auth.loginGoogle}
          variant="outline"
          className="w-full rounded-none h-11 bg-transparent border-white/15 hover:border-gold hover:bg-white/[0.03] text-white font-display uppercase tracking-widest"
        >
          Continue with Google
        </Button>

        <div className="mt-8 text-sm text-white/60">
          New to Songwriter Square?{" "}
          <Link
            to={registerHref(dest === "/app/dashboard" ? null : dest)}
            data-testid={TID.auth.toRegister}
            className="text-gold hover:text-gold-bright underline-offset-4 hover:underline"
          >
            Create an account
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
